import { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import SearchSelectBox from '@components/SearchSelectBox/SearchSelectBox';
import { IDietarySupplementTableData } from '@interfaces/supplements';
import { handleAdminSearchSupplements } from '@store/features/adminInquiries/supplementSearch/actions';
import {
  adminSupplementSearchIsLoadingSelector,
  adminSupplementSearchPaginationSelector,
  adminSupplementSearchSelector,
} from '@store/features/adminInquiries/supplementSearch/selectors';
import { removeAdminSupplementSearch } from '@store/features/adminInquiries/supplementSearch/slice';
import useDispatch from '@store/hooks/useDispatch';

import { ISearchAdminSupplementsProps } from './SearchAdminSupplements.interface';

import './style.scss';

const SearchAdminSupplements: FC<ISearchAdminSupplementsProps> = ({
  className,
  title,
  onSelect,
}) => {
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState('');
  const [value, setValue] = useState<IDietarySupplementTableData | null>(null);

  const items = useSelector(adminSupplementSearchSelector);
  const pagination = useSelector(adminSupplementSearchPaginationSelector);
  const isLoading = useSelector(adminSupplementSearchIsLoadingSelector);

  const handleSelect = useCallback(
    (event: SyntheticEvent, item: IDietarySupplementTableData | null) => {
      setValue(item);
      item && onSelect(item);

      if (!item) {
        setSearchValue('');
        dispatch(removeAdminSupplementSearch());
      }
    },
    [dispatch, onSelect],
  );

  const handleChange = useCallback(
    (value: string) => {
      dispatch(removeAdminSupplementSearch());
      setSearchValue(value);
    },
    [dispatch],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const currentPage = pagination?.page || 1;
      const pageCount = pagination?.pageCount || 0;

      if ((currentPage === pageCount && pageCount > 1) || value.length < 3) {
        return;
      }

      dispatch(
        handleAdminSearchSupplements({
          search: value,
          page: currentPage < pageCount ? currentPage + 1 : currentPage,
        }),
      );
    },
    [dispatch, pagination],
  );

  useEffect(() => {
    // Clear search field on initial load
    dispatch(removeAdminSupplementSearch());
  }, [dispatch]);

  useEffect(() => {
    handleSearch(searchValue);
  }, [searchValue]);

  return (
    <SearchSelectBox<IDietarySupplementTableData>
      className={className}
      title={title}
      items={items}
      searchValue={searchValue}
      onSearch={handleChange}
      selected={value}
      onSelect={handleSelect}
      onLoadMore={handleSearch}
      isLoading={isLoading}
      isMedicationSearch={false}
      showTitle={false}
    />
  );
};

export default SearchAdminSupplements;
